import { useCallback, useMemo, useState } from "react";

/**
 * Checkout selections that must survive leaving and re-entering the checkout
 * screen (Back preserves state — the design's core interaction). Owned by the
 * host (TransactionDetailModal / QuickSale) rather than CheckoutPanel so the
 * panel can unmount without losing them. Transient flow state (processing,
 * sub-modals, fetched lists) lives inside CheckoutPanel and may reset freely.
 *
 * `payMethod` reuses the legacy string sentinels so the charge dispatch ports
 * unchanged: "Card" | "CardOnFile" | "Cash" | "Package" | "GiftCard" |
 * "Bnpl:affirm" | an off-platform method name (e.g. "Zelle").
 */
export interface CheckoutState {
  payMethod: string | null;
  /** Card-on-file sub-selection (ClientPaymentMethod.id). */
  selectedCardId: number | null;
  /** Package sub-selection. */
  selectedPackageId: string | null;
  /**
   * Units to redeem per covered booked-service line of the selected package,
   * keyed by bookedServiceId. Multiple banked services in one package redeem
   * together; values may be fractional (per-syringe/unit).
   */
  packageRedemptions: Record<string, number>;
  /** "Charge a different amount" override; null = full balance. */
  overrideAmount: number | null;
  /**
   * Which physical terminal to charge on (Stripe reader device id). Resolved
   * from the location's terminals + the per-device last-used preference; null
   * until resolved (auto-selected when there is exactly one terminal).
   */
  selectedTerminalDeviceId: string | null;
}

export interface CheckoutStateActions {
  setPayMethod: (method: string | null) => void;
  setSelectedCardId: (id: number | null) => void;
  setSelectedPackageId: (id: string | null) => void;
  /** Set the redeem amount for a single covered line. */
  setPackageRedemption: (bookedServiceId: string, qty: number) => void;
  /** Replace the whole per-line map (package select defaulting / "Redeem max"). */
  setPackageRedemptions: (redemptions: Record<string, number>) => void;
  setOverrideAmount: (amount: number | null) => void;
  setSelectedTerminalDeviceId: (deviceId: string | null) => void;
  reset: () => void;
}

const INITIAL_STATE: CheckoutState = {
  payMethod: null,
  selectedCardId: null,
  selectedPackageId: null,
  packageRedemptions: {},
  overrideAmount: null,
  selectedTerminalDeviceId: null,
};

export function useCheckoutState(): {
  state: CheckoutState;
  actions: CheckoutStateActions;
} {
  const [state, setState] = useState<CheckoutState>(INITIAL_STATE);

  const setPayMethod = useCallback((method: string | null) => {
    setState((prev) => ({ ...prev, payMethod: method }));
  }, []);
  const setSelectedCardId = useCallback((id: number | null) => {
    setState((prev) => ({ ...prev, selectedCardId: id }));
  }, []);
  const setSelectedPackageId = useCallback((id: string | null) => {
    // Changing package invalidates the covered-line redeem amounts; they are
    // re-defaulted from the new package's balances by PackageReveal.
    setState((prev) => ({
      ...prev,
      selectedPackageId: id,
      packageRedemptions: {},
    }));
  }, []);
  const setPackageRedemption = useCallback(
    (bookedServiceId: string, qty: number) => {
      setState((prev) => ({
        ...prev,
        packageRedemptions: {
          ...prev.packageRedemptions,
          [bookedServiceId]: qty,
        },
      }));
    },
    [],
  );
  const setPackageRedemptions = useCallback(
    (redemptions: Record<string, number>) => {
      setState((prev) => ({ ...prev, packageRedemptions: redemptions }));
    },
    [],
  );
  const setOverrideAmount = useCallback((amount: number | null) => {
    setState((prev) => ({ ...prev, overrideAmount: amount }));
  }, []);
  const setSelectedTerminalDeviceId = useCallback((deviceId: string | null) => {
    setState((prev) => ({ ...prev, selectedTerminalDeviceId: deviceId }));
  }, []);
  const reset = useCallback(() => setState(INITIAL_STATE), []);

  const actions = useMemo(
    () => ({
      setPayMethod,
      setSelectedCardId,
      setSelectedPackageId,
      setPackageRedemption,
      setPackageRedemptions,
      setOverrideAmount,
      setSelectedTerminalDeviceId,
      reset,
    }),
    [
      setPayMethod,
      setSelectedCardId,
      setSelectedPackageId,
      setPackageRedemption,
      setPackageRedemptions,
      setOverrideAmount,
      setSelectedTerminalDeviceId,
      reset,
    ],
  );

  return { state, actions };
}
